import React, { useState, useEffect, createContext, useContext } from 'react'
import { useSnackbar } from 'notistack'
import { Grid, CssBaseline } from '@mui/material'

import { Outlet } from 'react-router-dom'
import AppBar from './AppBar'
import FixedBottomNavigation from './BottomNavigation'
import { usePushOwnershipSync } from '../hooks/usePushOwnershipSync'

type AppContextType = {
  toggleDrawer: () => void
}

const AppContext = createContext<AppContextType | undefined>(undefined)

const MainLayout = () => {
  // Shared-device safety + seamless restore: whoever is logged in owns this
  // browser's push subscription
  usePushOwnershipSync()
  const { enqueueSnackbar } = useSnackbar()
  // Day Snapshot (D15): a push that arrives while the app is open also shows as a toast
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined
    const onMessage = (event: MessageEvent) => {
      const message = event.data
      if (!message || message.kind !== 'oeta-push' || !message.payload) return
      const { title, body } = message.payload
      enqueueSnackbar(`${title || 'Oeta'}${body ? `. ${body}` : ''}`, {
        variant: 'info',
        autoHideDuration: 8000,
      })
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () =>
      navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [enqueueSnackbar])
  const [open, setOpen] = useState(false)
  const setIsOpen = () => {
    setOpen(!open)
  }

  const toggleDrawer = () => {
    setOpen(!open)
  }

  return (
    <AppContext.Provider value={{ toggleDrawer }}>
      <CssBaseline />
      <Grid
        container
        direction="column"
        sx={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <Grid item>
          {/* <SwipeableTemporaryDrawer isOpen={open} setOpen={setIsOpen} /> */}
          <AppBar setOpen={setIsOpen} />
        </Grid>
        {/* width 100% + minWidth 0: page content must never widen the layout past the
            viewport — flex items default to min-width auto and inherit their content's
            min-content width (long names, chip rows), which showed up as page-level
            horizontal scroll. maxWidth can't be used: Grid direction="column" injects
            `& > .MuiGrid-item { max-width: none }` which overrides it. */}
        <Grid item sx={{ p: 2, minWidth: 0, width: '100%' }}>
          <Outlet />
        </Grid>
        <Grid item>
          <FixedBottomNavigation />
        </Grid>
      </Grid>
    </AppContext.Provider>
  )
}

const useAppContext = () => {
  const contexts = useContext(AppContext)

  if (!contexts) {
    throw new Error('No App Contexts!')
  }

  return contexts
}

export { MainLayout, useAppContext }
